import { addressAndPort } from './address.ts';
import type { Logger } from '../logger.ts';
import { WebSocket } from 'ws';
import type { WebSocketHandshakeInfo } from '../../types/web/socket';

export class ExtendedWebSocket extends WebSocket {
    constructor(_first: unknown, _second?: unknown, options?: unknown) {
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        super(null as unknown as any, undefined, options as any);
    }

    connected?: boolean;
}

export type PingConfig = { interval: number, data?: 'timestamp' | 'empty' };

export class PingManager {
    static readonly #EMPTY_BUFFER = Buffer.alloc(0);
    static #LAST_TIMESTAMP_BUFFER: [number, Buffer] = [0, Buffer.alloc(8)];

    readonly #pingData: () => Buffer;
    readonly #pingInterval: number | undefined;
    readonly #pingIntervalId?: ReturnType<typeof setInterval>;
    readonly #mask = false;
    readonly #logger: Logger;

    constructor(logger: Logger, supplier: () => [string, Set<ExtendedWebSocket>], ping?: number | PingConfig) {
        this.#logger = logger;
        this.#pingInterval = typeof ping === 'number' ? ping : ping?.interval;
        this.#pingData =
            typeof ping === 'number' || ping?.data === 'timestamp'
                ? () => PingManager.#createBufferFromTimestamp(Date.now())
                : () => PingManager.#EMPTY_BUFFER;

        if (this.#pingInterval) {
            this.#pingIntervalId = setInterval(() => {
                const [path, clients] = supplier();
                for (const client of clients) {
                    if (this.#terminateIfDisconnected(client, path)) {
                        continue;
                    }
                    this.#markAndSendPing(client, path);
                }
            }, this.#pingInterval);

        }
    }

    #terminateIfDisconnected(client: ExtendedWebSocket, path: string) {
        if (client.connected === false) {
            if (this.#logger.enabledFor('debug')) {
                this.#logger.debug(`terminating unresponsive ws client on [${path}]`);
            }
            client.terminate();
            return true;
        }
        return false;
    }

    #markAndSendPing(client: ExtendedWebSocket, path: string) {
        client.connected = false;
        const data = this.#pingData();
        if (this.#logger.enabledFor('trace')) {
            this.#logger.debug(`pinging ws client on [${path}]`);
        }
        client.ping(data, this.#mask, (err: Error) => {
            if (err && this.#logger.enabledFor('warn')) {
                this.#logger.warn(`failed to ping ws client on [${path}]`, err);
            }
        });
    }

    static #createBufferFromTimestamp(now = Date.now()): Buffer {
        if (now - PingManager.#LAST_TIMESTAMP_BUFFER[0] > 0) {
            const buffer = Buffer.allocUnsafe(8);
            buffer.writeBigInt64BE(BigInt(now), 0);
            PingManager.#LAST_TIMESTAMP_BUFFER = [now, buffer];
        }
        return PingManager.#LAST_TIMESTAMP_BUFFER[1];
    }

    static #createTimestampFromBuffer(data: Buffer): number {
        if (data.length === 8) {
            return Number(data.readBigInt64BE(0));
        }
        return 0;
    }

    close() {
        clearInterval(this.#pingIntervalId);
    }

    handlePing(handshake: WebSocketHandshakeInfo, socket: ExtendedWebSocket, data: Buffer): void {
        socket.connected = true;
        socket.pong(data, false, (err: Error) => {
            if (err && this.#logger.enabledFor('warn')) {
                this.#logger.warn(`${handshake.logPrefix}failed to pong ws client ${addressAndPort(handshake.remoteAddress)}`, err);
            }
        });
    }

    handlePong(handshake: WebSocketHandshakeInfo, socket: ExtendedWebSocket, data: Buffer): void {
        socket.connected = true;
        if (this.#logger.enabledFor('warn')) {
            const sent = PingManager.#createTimestampFromBuffer(data);
            if (sent > 0) {
                const latency = Date.now() - sent;
                if (this.#logger.enabledFor('debug')) {
                    this.#logger.debug(`${handshake.logPrefix}ws client ${addressAndPort(handshake.remoteAddress)} ping-pong latency: ${latency}ms`);
                }
                if (this.#pingInterval && latency > this.#pingInterval / 2 && this.#logger.enabledFor('warn')) {
                    this.#logger.warn(`${handshake.logPrefix}ws client ${addressAndPort(handshake.remoteAddress)} high ping-pong latency: ${latency}ms`);
                }
            }
        }
    }
}
